var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { OnInit, EventEmitter, ElementRef, OnDestroy } from '@angular/core';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { PublishService } from '../../publish.service';
import { CameraService } from 'src/app/engine/camera.service';
import { Observable, Subject, interval } from 'rxjs';
import { map, takeUntil, filter, tap } from 'rxjs/operators';
import { WidgetOptions } from '../../models/widget-options.model';
import { animationFrame } from 'rxjs/internal/scheduler/animationFrame';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { MessageEnum } from 'src/app/widget-api/models/message.enum';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { LoaderTypeEnum } from 'src/app/view/models/loader-type.enum';
import { ScrollModeEnum } from 'src/app/engine/models/scroll-mode.enum';
var scaleSmoothFactor = 0.95;
var PublishWidgetComponent = /** @class */ (function () {
    function PublishWidgetComponent(elRef, publishService, cameraService) {
        this.elRef = elRef;
        this.publishService = publishService;
        this.cameraService = cameraService;
        this.scalable = false;
        this.cameraUpdated = new EventEmitter();
        this.ngUnsubscribe = new Subject();
        this._ready = false;
        this._enableUI = true;
    }
    Object.defineProperty(PublishWidgetComponent.prototype, "enableUI", {
        set: function (value) {
            this._enableUI = value;
            this.setWidgetUIConfig(this._enableUI, this._params);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "bgColor", {
        set: function (value) {
            this._bgColor = value;
            if (this._ready) {
                this.renderWidgetBGColor();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "widgetParams", {
        set: function (value) {
            this._params = value;
            this._rotateDegrees = value ? value.loadRotate : 0;
            this.setWidgetUIConfig(this._enableUI, this._params);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "topOffset", {
        get: function () {
            var containerHeight = this.elRef.nativeElement.offsetHeight;
            var targetOffset = (containerHeight - this.height) / 2;
            return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "leftOffset", {
        get: function () {
            var containerWidth = this.elRef.nativeElement.offsetWidth;
            var targetOffset = (containerWidth - this.width) / 2;
            return this.scaleFactor < 1 ? targetOffset : Math.max(0, targetOffset);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PublishWidgetComponent.prototype, "scaleFactor", {
        get: function () {
            if (!this.scalable) {
                return 1;
            }
            else {
                var _a = this.containerRef.nativeElement, offsetWidth = _a.offsetWidth, offsetHeight = _a.offsetHeight;
                var xScale = offsetWidth / this.width;
                var yScale = offsetHeight / this.height;
                var targetScale = Math.min(xScale, yScale, 1);
                if (targetScale === 1) { // default scale
                    return 1;
                }
                else { // scaled model: smooth
                    return targetScale * scaleSmoothFactor;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    PublishWidgetComponent.prototype.ngOnInit = function () {
        var _this = this;
        var frameId = this.frameRef.nativeElement.id;
        this._apiRef = new HumanAPI({ iframeId: frameId });
        // listen for API ready state
        this.getAPIEvent(EventsEnum.HumanReady)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function () {
            // ready state
            _this._ready = true;
            _this.setupCameraTracking();
            _this.setupCameraRotation();
            _this.setWidgetUIConfig(_this._enableUI, _this._params);
            // setup background color
            _this.getCurrentBGColor()
                .pipe(takeUntil(_this.ngUnsubscribe))
                .subscribe(function (color) {
                _this._defaultColor = color;
                // initial bg color
                _this.renderWidgetBGColor();
            });
        });
    };
    PublishWidgetComponent.prototype.ngOnDestroy = function () {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    };
    PublishWidgetComponent.prototype.setWidgetUIConfig = function (enableUI, widgetOptions) {
        // API ready
        if (this._apiRef && widgetOptions) {
            if (enableUI) { // set UI form user selection
                var uiDisplay = this.convertWidgetOptionsToDisplayParams(widgetOptions);
                this.resetDisplay();
                this.setUIDisplay(uiDisplay);
                // set loading experience
                var loadingExperience = this.getLoadingExperience(widgetOptions.initial);
                var scrollCaptureMode = this.getScrollCaptureMode(widgetOptions.disableScroll);
                this._apiRef.send(MessageEnum.UISetLoadingExperience, loadingExperience);
                this._apiRef.send(MessageEnum.UISetScrollCaptureMode, scrollCaptureMode);
            }
            else { // disable UI + loading experience
                this.hideUIDisplay();
                var loadingExperience = this.getLoadingExperience({ button: false, handHint: false, play: false });
                var scrollCaptureMode = this.getScrollCaptureMode({ alert: false, overlay: false });
                this._apiRef.send(MessageEnum.UISetLoadingExperience, loadingExperience);
                this._apiRef.send(MessageEnum.UISetScrollCaptureMode, scrollCaptureMode);
            }
        }
    };
    PublishWidgetComponent.prototype.takeSnapshot = function (dimensions) {
        return this.getAPIData(MessageEnum.UISnapshot, __assign({ width: this.width, height: this.height }, dimensions));
    };
    /**
     * Orbits the camera around the model by specified degrees.
     */
    PublishWidgetComponent.prototype.setCameraOrbit = function (degrees) {
        this.sendAPIData(MessageEnum.CameraOrbit, {
            yaw: degrees
        });
    };
    /**
   * Sets UI options (i.e., fullscreen, help, etc...)
   */
    PublishWidgetComponent.prototype.renderWidgetBGColor = function () {
        this.setWidgetBGColor(this._bgColor || this._defaultColor);
    };
    PublishWidgetComponent.prototype.setWidgetBGColor = function (background) {
        if (this._apiRef && background) {
            this.sendAPIData(MessageEnum.UISetBackground, background);
        }
    };
    PublishWidgetComponent.prototype.setupCameraTracking = function () {
        var _this = this;
        // get initial camera
        this.getCurrentCamera()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function (currentCamera) {
            // set fixed default camera
            _this.setDefaultCamera(currentCamera);
            // begin tracking camera changes
            _this.getCameraUpdates()
                .pipe(takeUntil(_this.ngUnsubscribe))
                .subscribe(function (updateCamera) {
                _this.setUpdateCamera(updateCamera);
            });
        });
    };
    // use API to rotate widget fixed amount in an interval
    PublishWidgetComponent.prototype.setupCameraRotation = function () {
        var _this = this;
        // setup camera rotate animation
        var timeLast = performance.now();
        interval(0, animationFrame)
            .pipe(filter(function () { return !!_this._rotateDegrees; }), // on valid rotation values
        map(function () { return _this._rotateDegrees * (performance.now() - timeLast) * 0.001; }), tap(function () { return timeLast = performance.now(); }), takeUntil(this.ngUnsubscribe))
            .subscribe(function (degrees) {
            _this.setCameraOrbit(degrees);
        });
    };
    PublishWidgetComponent.prototype.setDefaultCamera = function (camera) {
        this._defaultCamera = camera;
        this.setUpdateCamera(camera);
    };
    PublishWidgetComponent.prototype.setUpdateCamera = function (camera) {
        this._currentCamera = camera;
        // #TODO: compare cameras, smooth ???
        var camerasMatch = this.cameraService.compareCameras(this.cameraService.roundCamera(this._defaultCamera, 3), this.cameraService.roundCamera(this._currentCamera, 3));
        // dispatch changes: validate delta from original
        var targetCamera = !camerasMatch ? this._currentCamera : null;
        this.cameraUpdated.emit(targetCamera);
    };
    PublishWidgetComponent.prototype.getAPIEvent = function (event) {
        var _this = this;
        return new Observable(function (observer) {
            _this._apiRef.on(event, function (data) {
                observer.next(data);
            });
        });
    };
    PublishWidgetComponent.prototype.getAPIData = function (message, payload) {
        var _this = this;
        return new Observable(function (observer) {
            _this._apiRef.send(message, payload, function (data) {
                observer.next(data);
                observer.complete();
            });
        });
    };
    PublishWidgetComponent.prototype.sendAPIData = function (message, payload) {
        this._apiRef.send(message, payload);
    };
    PublishWidgetComponent.prototype.getCurrentCamera = function () {
        return this.getAPIData(MessageEnum.CameraInfo)
            .pipe(map(function (data) {
            return {
                eye: data.position,
                look: data.target,
                up: data.up
            };
        }));
    };
    PublishWidgetComponent.prototype.getCurrentBGColor = function () {
        return this.getAPIData(MessageEnum.UIGetBackground)
            .pipe(map(function (data) {
            if (data) {
                return new BackgroundModel(__spread([].concat.apply([], __spread(data.colors))), // [[r,g,b,a], [r,g,b,a]] -> [r,g,b,a,r,g,b,a]
                data.gradientType, data.colorStops);
            }
            else {
                return null;
            }
        }));
    };
    PublishWidgetComponent.prototype.getCameraUpdates = function () {
        return this.getAPIEvent(EventsEnum.CameraUpdated)
            .pipe(map(function (data) {
            var position = data.position, target = data.target, up = data.up;
            return {
                eye: position,
                look: target,
                up: up
            };
        }));
    };
    PublishWidgetComponent.prototype.setUIDisplay = function (uiDisplay) {
        this._apiRef.send(MessageEnum.UISetDisplay, uiDisplay);
    };
    PublishWidgetComponent.prototype.resetDisplay = function () {
        this._apiRef.send(MessageEnum.UIResetDisplay);
    };
    PublishWidgetComponent.prototype.hideUIDisplay = function () {
        this.setUIDisplay({
            info: false,
            search: false,
            reset: false,
            fullscreen: false,
            nav: false,
            tools: false,
            help: false,
            chapterList: false,
            labelList: false,
            anatomyDescriptions: false,
            tour: false,
            media: false,
            menu: false
        });
    };
    PublishWidgetComponent.prototype.convertWidgetOptionsToDisplayParams = function (params) {
        return {
            info: params.uiInfo,
            search: params.uiSearch,
            reset: params.uiReset,
            fullscreen: params.uiFullscreen,
            nav: params.uiNav,
            tools: params.uiTools,
            help: params.uiHelp,
            chapterList: params.uiChapterList,
            labelList: params.uiLabelList,
            anatomyDescriptions: this._params.uiAnatomyDescriptions
        };
    };
    PublishWidgetComponent.prototype.getScrollCaptureMode = function (disableScroll) {
        if (disableScroll.alert) {
            return ScrollModeEnum.DoNotCaptureShowMessage;
        }
        if (disableScroll.overlay) {
            return ScrollModeEnum.DoNotCapture;
        }
        return ScrollModeEnum.Capture;
    };
    PublishWidgetComponent.prototype.getLoadingExperience = function (initial) {
        if (initial.button) {
            return LoaderTypeEnum.Button;
        }
        if (initial.handHint) {
            return LoaderTypeEnum.Hand;
        }
        if (initial.play) {
            return LoaderTypeEnum.Play;
        }
        return undefined;
    };
    return PublishWidgetComponent;
}());
export { PublishWidgetComponent };
